import type { Delivery, MessageTemplate } from '@contract';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Cell, StatusBadge } from '@/components/record-table';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loading } from '@/components/ui/loading';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableFrame,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TablePager, usePaging } from '@/components/ui/table-pager';
import { callAdmin } from '@/lib/api';
import { adminQuery, invalidate } from '@/lib/query';
import { useCan } from '@/lib/staff-context';
import { toast } from '@/lib/toast';

/**
 * Sends one customer a message from a template: in-app and push, and SMS
 * when the template allows it. From a case, the case notes it.
 */
export function SendMessage({
  customerId,
  caseId,
  issueId,
}: Readonly<{ customerId: string; caseId?: string; issueId?: string }>) {
  const [open, setOpen] = useState(false);
  if (!useCan('messages.send')) return null;
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        Send a message
      </Button>
      {open ? (
        <SendDialog
          customerId={customerId}
          caseId={caseId}
          issueId={issueId}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

function SendDialog({
  customerId,
  caseId,
  issueId,
  onClose,
}: Readonly<{
  customerId: string;
  caseId?: string;
  issueId?: string;
  onClose: () => void;
}>) {
  const templates = useQuery(adminQuery('messages.templates', {}));
  const [code, setCode] = useState('');
  const [sms, setSms] = useState(false);
  const template: MessageTemplate | undefined = templates.data?.find(
    (row) => row.code === (code || templates.data?.[0]?.code),
  );
  const send = useMutation({
    mutationFn: async () => {
      if (!template) throw new Error('Pick a template.');
      const sent = await callAdmin('messages.send', {
        customer_id: customerId,
        template: template.code,
        sms: sms && template.allow_sms,
        case_id: caseId,
        issue_id: issueId,
      });
      if (caseId) {
        await callAdmin('cases.update', {
          case_id: caseId,
          message: template.code,
        });
      }
      return sent;
    },
    onSuccess: async () => {
      await invalidate('messages.deliveries', 'cases.get');
      toast.success('Message queued.');
      onClose();
    },
  });

  return (
    <Dialog open onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send a message</DialogTitle>
          <DialogDescription>
            It shows in the customer's Sao app and goes by push. It is logged
            against you.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Select
            label="Template"
            value={template?.code ?? ''}
            onChange={(event) => setCode(event.target.value)}
          >
            {templates.data?.map((row) => (
              <option key={row.code} value={row.code}>
                {row.name}
              </option>
            ))}
          </Select>
        </div>
        {template ? (
          <div className="rounded-lg border bg-surface-2/40 p-3 text-sm">
            <p className="font-medium">{template.title}</p>
            <p className="text-muted-foreground">{template.body}</p>
          </div>
        ) : null}
        <Checkbox
          checked={sms && Boolean(template?.allow_sms)}
          disabled={!template?.allow_sms}
          onCheckedChange={(value) => setSms(value === true)}
        >
          {template?.allow_sms
            ? 'Also by SMS'
            : 'This template does not go by SMS'}
        </Checkbox>
        {send.error ? (
          <Alert variant="destructive">{send.error.message}</Alert>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!template || send.isPending}
            onClick={() => send.mutate()}
          >
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Every notice to a customer: recorded, push receipts, SMS answers. */
export function DeliveryLog({ customerId }: Readonly<{ customerId: string }>) {
  const log = useQuery(
    adminQuery('messages.deliveries', { customer_id: customerId }),
  );
  const paging = usePaging(log.data);
  if (log.isError) {
    return <Alert variant="destructive">{log.error.message}</Alert>;
  }
  if (!log.data) {
    return <Loading />;
  }
  if (log.data.length === 0) {
    return <p className="text-sm text-muted-foreground">Nothing sent yet.</p>;
  }
  return (
    <TableFrame>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Queued</TableHead>
            <TableHead>Notice</TableHead>
            <TableHead>Recorded</TableHead>
            <TableHead>Push</TableHead>
            <TableHead>SMS</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paging.visible?.map((row) => (
            <TableRow key={row.id}>
              <TableCell label="Queued">
                <Cell kind="datetime" value={row.created_at} />
              </TableCell>
              <TableCell label="">
                <div className="font-medium">{row.title}</div>
                <div className="font-mono text-xs text-muted-foreground">
                  {row.event}
                </div>
              </TableCell>
              <TableCell label="Recorded">
                {row.delivered_at ? (
                  <Cell kind="datetime" value={row.delivered_at} />
                ) : (
                  <span className="text-xs text-muted-foreground">
                    {row.last_error ?? `Waiting (${row.attempts} tries)`}
                  </span>
                )}
              </TableCell>
              <TableCell label="Push">
                <PushCell push={row.push} />
              </TableCell>
              <TableCell label="SMS">
                <SmsCell row={row} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePager paging={paging} />
    </TableFrame>
  );
}

function PushCell({ push }: Readonly<{ push: Delivery['push'] }>) {
  if (push.length === 0) {
    return <span className="text-xs text-muted-foreground">None</span>;
  }
  return (
    <div className="flex flex-col gap-1">
      {push.map((ticket) => (
        <span key={ticket.sent_at} className="flex items-center gap-2">
          <StatusBadge status={ticket.receipt ?? 'sent'} />
          {ticket.error ? (
            <span className="font-mono text-xs">{ticket.error}</span>
          ) : null}
        </span>
      ))}
    </div>
  );
}

function SmsCell({ row }: Readonly<{ row: Delivery }>) {
  if (!row.sms_requested) {
    return <span className="text-xs text-muted-foreground">Not asked</span>;
  }
  if (row.sms.length === 0) {
    return <span className="text-xs text-muted-foreground">No answer yet</span>;
  }
  return (
    <div className="flex flex-col gap-1">
      {row.sms.map((answer) => (
        <span key={answer.at} className="flex items-center gap-2">
          <StatusBadge status={answer.status} />
          <span className="text-xs text-muted-foreground">
            {answer.provider}
            {answer.detail ? `: ${answer.detail}` : ''}
          </span>
        </span>
      ))}
    </div>
  );
}
